import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Plus, WifiOff } from 'lucide-react';
import type { ReportWizardApi, ReportWizardLead, ReportWizardLeads, ReportWizardPhoto, ReportWizardProps } from './types';
import { useReportDraftState } from './useReportDraftState';
import { LeadCard } from './LeadCard';
import { DraftCard } from './DraftCard';
import { SectionCard } from './SectionCard';
import { PhotoCard } from './PhotoCard';
import { PhotoClassifyNotice } from './PhotoClassifyNotice';
import { ReviseCard, type Revision } from './ReviseCard';
import { PdfCard } from './PdfCard';
import { DeliverCard, useDeliverState } from './DeliverCard';
import { SECONDARY } from './ui';
import { httpsUrlOrNull } from './safeUrl';
import { MAX_SECTIONS } from './useReportDraftState';

type Busy = 'idle' | 'classifying' | 'drafting' | 'rewriting' | 'generating-pdf' | 'saving-drive';

const message = (err: unknown, fallback: string) => (err instanceof Error ? err.message : fallback);

/**
 * The site-visit report wizard used by both the Sales app and the main app.
 * One scrolling page: lead, draft, sections, photos, revise, PDF, deliver.
 * Host apps key it by survey id and pass the survey as loaded.
 */
export function ReportWizard(props: ReportWizardProps) {
  const { survey, isOnline, notify } = props;
  // Hosts may pass new api/leads objects on every render; read the latest through
  // refs so effects and callbacks never re-run (or refetch) because of that.
  const apiRef = useRef(props.api);
  apiRef.current = props.api;
  const leadsRef = useRef(props.leads);
  leadsRef.current = props.leads;
  const api = useMemo<ReportWizardApi>(() => ({
    draft: (a) => apiRef.current.draft(a),
    rewriteSection: (a) => apiRef.current.rewriteSection(a),
    reviseAll: (a) => apiRef.current.reviseAll(a),
    saveDraft: (u) => apiRef.current.saveDraft(u),
    generatePdf: () => apiRef.current.generatePdf(),
    saveToDrive: (id) => apiRef.current.saveToDrive(id),
    send: (a) => apiRef.current.send(a),
    classifyPhotos: (p, onProgress) => (apiRef.current.classifyPhotos ? apiRef.current.classifyPhotos(p, onProgress) : Promise.resolve([])),
  }), []);
  const canClassify = Boolean(props.api.classifyPhotos);
  const leads = useMemo<ReportWizardLeads>(() => ({
    search: (t) => leadsRef.current.search(t),
    read: (id) => leadsRef.current.read(id),
  }), []);
  const layout = props.layout ?? 'phone';
  const confirm = props.confirm ?? ((m: string) => window.confirm(m));

  const state = useReportDraftState(survey.reportDraft ?? null, api.saveDraft);
  const { draft } = state;
  const [reportUrl, setReportUrl] = useState<string | null>(draft ? httpsUrlOrNull(survey.reportUrl) : null);
  const [lead, setLead] = useState<ReportWizardLead | null>(null);
  // A survey that HAS a lead waits for it to load (or fail) before drafting, so a slow read
  // never produces a draft that silently forgets its customer. No lead on the survey: ready now.
  const [leadSettled, setLeadSettled] = useState(!survey.leadId);
  const [busy, setBusy] = useState<Busy>('idle');
  const [error, setError] = useState<string | null>(null);
  const [revisions, setRevisions] = useState<Revision[]>([]);
  const [drive, setDrive] = useState<{ driveFileUrl: string; driveFolderUrl: string } | null>(null);
  const deliver = useDeliverState();

  // The survey's lead loads with the wizard, whatever state it resumes in.
  useEffect(() => {
    if (!survey.leadId) { setLeadSettled(true); return undefined; }
    let live = true;
    setLeadSettled(false);
    leads.read(survey.leadId)
      .then((found) => { if (live && found) setLead(found); })
      .catch((err) => console.warn('ReportWizard: lead load failed', err))
      .finally(() => { if (live) setLeadSettled(true); });
    return () => { live = false; };
  }, [survey.leadId, leads]);

  // Photos classified since the draft was made: the draft does not include them until it is redrafted.
  const [classifiedSinceDraft, setClassifiedSinceDraft] = useState(0);
  // Photos classified from this screen: the survey prop is read once, so what classification found is kept here.
  const [classifiedNow, setClassifiedNow] = useState<ReadonlyMap<string, { category?: string; description?: string }>>(new Map());
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const withClassification = useMemo<ReportWizardPhoto[]>(
    () => (survey.intakePhotos ?? []).map((p) => {
      const found = classifiedNow.get(p.photoId);
      return found ? { ...p, status: 'classified', ...found } : p;
    }),
    [survey.intakePhotos, classifiedNow],
  );
  const photos = useMemo(() => withClassification.filter((p) => p.status === 'classified' && Boolean(p.url)), [withClassification]);
  // A photo with no storage path cannot be classified from here (the host needs the path), so it is not offered.
  const waiting = useMemo(() => withClassification.filter((p) => p.status !== 'classified' && Boolean(p.url)), [withClassification]);
  const classifiable = useMemo(
    () => waiting.filter((p): p is ReportWizardPhoto & { storageRef: string } => typeof p.storageRef === 'string' && p.storageRef.length > 0),
    [waiting],
  );

  /** Classify what is waiting. Never throws: a photo that fails stays waiting (retryable) and is counted in the message. */
  const classifyWaiting = useCallback(async (asToast = false): Promise<void> => {
    if (!apiRef.current.classifyPhotos || classifiable.length === 0) return;
    setBusy('classifying');
    setProgress({ done: 0, total: classifiable.length });
    try {
      const outcomes = await apiRef.current.classifyPhotos!(
        classifiable.map((p) => ({ photoId: p.photoId, storageRef: p.storageRef })),
        (done, total) => setProgress({ done, total }),
      );
      const good = outcomes.filter((o) => o.ok);
      if (good.length > 0) {
        setClassifiedSinceDraft((n) => n + good.length);
        setClassifiedNow((prev) => {
          const next = new Map(prev);
          for (const o of good) next.set(o.photoId, { ...(o.category ? { category: o.category } : {}), ...(o.description ? { description: o.description } : {}) });
          return next;
        });
      }
      const failed = classifiable.length - good.length;
      if (failed > 0) notify.error(`${failed} ${failed === 1 ? 'photo' : 'photos'} could not be classified. They stay as they are: try Classify now again.`);
    } catch (err) {
      const text = message(err, 'Photo classification failed. Try Classify now again.');
      // On the generate path the draft starts right after, so the problem is a toast the draft cannot wipe; on the button it is inline.
      if (asToast) notify.error(text);
      else setError(text);
    } finally {
      setProgress(null);
      setBusy('idle');
    }
  }, [classifiable, notify]);

  const runDraft = useCallback(async ({ guidance, redraft, keepPhotos }: { guidance?: string; redraft: boolean; keepPhotos: boolean }) => {
    if (!leadSettled) return false;
    if (redraft && !confirm('Redraft the report with AI? Manual section edits will be replaced.')) return false;
    // The draft only uses classified photos: classify what is waiting first, so a rep who did not tap anything still gets photos.
    setError(null);
    if (!redraft) await classifyWaiting(true);
    setBusy('drafting');
    try {
      const next = await api.draft({
        // A survey that HAS a lead always sends it, even when the client could not read it
        // (restricted, offline blip): the server resolves or refuses it, never a silent
        // customerless draft. Only a survey with no lead at all drafts without one.
        ...((lead?.leadId ?? survey.leadId) ? { leadId: (lead?.leadId ?? survey.leadId) as string } : {}),
        ...(guidance ? { guidance } : {}),
        ...(redraft ? { preservePhotoSelections: keepPhotos } : {}),
      });
      state.replace(next);
      setClassifiedSinceDraft(0);
      notify.success(redraft ? 'Report redrafted!' : 'Report draft created!');
      return true;
    } catch (err) {
      setError(message(err, 'Failed to generate draft'));
      return false;
    } finally {
      setBusy('idle');
    }
  }, [lead, leadSettled, survey.leadId, confirm, api, state, notify, classifyWaiting]);

  const rewrite = useCallback(async (sectionId: string, currentContent: string, instructions: string) => {
    setBusy('rewriting');
    setError(null);
    try {
      const content = await api.rewriteSection({ sectionId, currentContent, instructions });
      state.replace((d) => ({ ...d, sections: d.sections.map((s) => (s.id === sectionId ? { ...s, content } : s)), updatedAt: new Date().toISOString() }));
      notify.success('Section rewritten');
    } catch (err) {
      setError(message(err, 'Rewrite failed'));
    } finally {
      setBusy('idle');
    }
  }, [api, state, notify]);

  const revise = useCallback(async (instructions: string) => {
    if (!instructions) return false;
    const number = revisions.length + 1;
    setRevisions((prev) => [...prev, { number, feedback: instructions, at: new Date().toISOString() }]);
    setError(null);
    try {
      // One batched server call (audit finding D2: a per-section fan-out lost rewrites).
      const sections = await api.reviseAll({ instructions });
      state.replace((d) => ({ ...d, sections, updatedAt: new Date().toISOString() }));
      notify.success(`Revision ${number} applied`);
      return true;
    } catch (err) {
      setError(message(err, 'Revision failed'));
      return false;
    }
  }, [revisions.length, api, state, notify]);

  const generatePdf = useCallback(async () => {
    setBusy('generating-pdf');
    setError(null);
    setReportUrl(null);
    try {
      try {
        await state.flush(); // throws if the latest edits did not save
      } catch (err) {
        setError(`Your latest edits did not save, so the PDF was not made (${message(err, 'save failed')}). Try again.`);
        return;
      }
      const url = httpsUrlOrNull(await api.generatePdf());
      if (!url) {
        setError('The PDF link that came back was not a secure (https) link, so it is not shown. Try again.');
        return;
      }
      setReportUrl(url);
      deliver.setSentIsCurrent(false); // a new PDF has not been sent yet
      notify.success('PDF generated!');
    } catch (err) {
      setError(message(err, 'PDF generation failed'));
    } finally {
      setBusy('idle');
    }
  }, [state, api, notify, deliver]);

  const saveDrive = useCallback(async () => {
    if (!lead) return;
    setBusy('saving-drive');
    setError(null);
    try {
      setDrive(await api.saveToDrive(lead.leadId));
      notify.success('Saved to Drive!');
    } catch (err) {
      setError(message(err, 'Drive save failed'));
    } finally {
      setBusy('idle');
    }
  }, [lead, api, notify]);

  const sections = draft ? [...draft.sections].sort((a, b) => a.order - b.order) : [];

  return (
    <div className="space-y-4">
      {error && (
        <div role="alert" className="flex items-center gap-2 rounded-xl bg-red-50 px-4 py-2 text-sm text-red-700">
          <span className="flex-1">{error}</span>
          <button type="button" onClick={() => setError(null)} className="min-h-[44px] px-2 font-semibold text-red-800">Dismiss</button>
        </div>
      )}
      {!isOnline && (
        <div className="flex items-center gap-2 rounded-xl bg-amber-50 px-4 py-3 text-sm text-amber-700">
          <WifiOff size={16} /> You&apos;re offline. Report generation requires a connection.
        </div>
      )}

      <LeadCard lead={lead} leads={leads} onSelect={setLead} />

      <PhotoClassifyNotice
        waiting={waiting.length}
        total={withClassification.length}
        hasDraft={Boolean(draft)}
        classifiedSinceDraft={classifiedSinceDraft}
        canClassify={canClassify && classifiable.length > 0}
        isOnline={isOnline}
        isClassifying={busy === 'classifying'}
        progress={progress}
        onClassify={() => void classifyWaiting(false)}
      />

      <DraftCard hasDraft={Boolean(draft)} isOnline={isOnline && leadSettled} isDrafting={busy === 'drafting' || busy === 'classifying'} onDraft={runDraft} />

      {draft && (
        <>
          <h3 className="px-1 text-sm font-bold text-slate-900">Report Sections</h3>
          {sections.map((section, i) => (
            <SectionCard
              key={section.id}
              section={section}
              isFirst={i === 0}
              isLast={i === sections.length - 1}
              isOnline={isOnline}
              isRewriting={busy === 'rewriting'}
              renderSectionEditor={props.renderSectionEditor}
              onUpdate={state.updateSection}
              onDelete={state.deleteSection}
              onMove={state.moveSection}
              onRewrite={rewrite}
            />
          ))}
          <button type="button" onClick={state.addSection} disabled={draft.sections.length >= MAX_SECTIONS} className={`${SECONDARY} w-full`}>
            <Plus size={16} /> Add section
          </button>
          {draft.sections.length >= MAX_SECTIONS && (
            <p className="px-1 text-xs text-slate-500">A report can have up to {MAX_SECTIONS} sections.</p>
          )}

          <PhotoCard selections={draft.photoSelections} photos={photos} waitingCount={waiting.length} sections={draft.sections} onToggle={state.togglePhoto} onAssign={state.assignPhoto} />

          {!reportUrl && <ReviseCard isOnline={isOnline} revisions={revisions} onRevise={revise} />}

          <PdfCard
            reportUrl={reportUrl}
            canGenerate
            isOnline={isOnline}
            isGenerating={busy === 'generating-pdf'}
            layout={layout}
            onGenerate={() => void generatePdf()}
          />

          {reportUrl && (
            <DeliverCard
              isOnline={isOnline}
              hasLead={Boolean(lead)}
              cover={draft.coverEmailDraft}
              api={api}
              isSavingDrive={busy === 'saving-drive'}
              drive={drive}
              onSaveDrive={() => void saveDrive()}
              onRegenerate={() => void generatePdf()}
              onError={setError}
              onSent={(recipient) => notify.success(`Report sent to ${recipient}`)}
              onDone={() => props.onDone(lead?.leadId ?? survey.leadId ?? null)}
              form={deliver}
            />
          )}
        </>
      )}
    </div>
  );
}
